import * as React from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { Typography } from '@/components/common';
import { CaseProgressStepperProps, CaseStepState } from '@/interfaces';

const STATE_TEXT: Record<CaseStepState, string> = {
	completed: 'completed',
	current: 'current step',
	upcoming: 'upcoming',
};

export const CaseProgressStepper: React.FC<CaseProgressStepperProps> = ({
	steps,
	disabled,
	className,
}) => {
	const currentPosition = steps.findIndex((step) => step.state === 'current');
	const currentStep = steps[currentPosition];
	const activeBg = disabled ? 'bg-grey-70' : 'bg-primary';

	return (
		<div className={className}>
			<div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
				<Typography type="semibold-14" className="uppercase tracking-wider">
					Case progress
				</Typography>
				{currentStep && (
					<Typography type="regular-12" className="break-words text-gray-35">
						{`Step ${currentPosition + 1} of ${steps.length} · ${
							currentStep.name
						}`}
					</Typography>
				)}
			</div>
			<div
				role="region"
				aria-label="Case progress steps"
				tabIndex={0}
				className="mt-3 overflow-x-auto scroll-custom rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
			>
				<ol
					aria-label="Case progress"
					className="m-0 flex list-none px-1 pb-1 pt-2"
				>
					{steps.map((step, index) => {
						const isFirst = index === 0;
						const isLast = index === steps.length - 1;
						const isReached = step.state !== 'upcoming';
						const isCompleted = step.state === 'completed';

						return (
							<li
								key={`${step.name}-${index}`}
								aria-current={step.state === 'current' ? 'step' : undefined}
								className="flex min-w-24 flex-1 flex-col items-center"
							>
								<div className="flex w-full items-center">
									<span
										aria-hidden="true"
										className={clsx('h-0.5 flex-1', {
											'bg-transparent': isFirst,
											[activeBg]: !isFirst && isReached,
											'bg-grey-100': !isFirst && !isReached,
										})}
									/>
									<span
										aria-hidden="true"
										className={clsx(
											'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
											isReached ? activeBg : 'bg-grey-100',
											{
												'ring-4': step.state === 'current',
												'ring-purple-35': step.state === 'current' && !disabled,
												'ring-grey-100': step.state === 'current' && disabled,
											}
										)}
									>
										{isReached ? (
											<Check
												aria-hidden="true"
												className="h-3 w-3 text-white"
												strokeWidth={3}
											/>
										) : (
											<span className="h-2 w-2 rounded-full bg-opacity-primary" />
										)}
									</span>
									<span
										aria-hidden="true"
										className={clsx('h-0.5 flex-1', {
											'bg-transparent': isLast,
											[activeBg]: !isLast && isCompleted,
											'bg-grey-100': !isLast && !isCompleted,
										})}
									/>
								</div>
								<Typography
									type={isReached ? 'semibold-12' : 'regular-10'}
									className={clsx('mt-1.5 px-1 text-center break-words', {
										'text-primary': isReached && !disabled,
										'text-grey-70': isReached && disabled,
										'text-gray-35': !isReached,
									})}
								>
									{step.name}
									<span className="sr-only">{`, ${
										STATE_TEXT[step.state]
									}`}</span>
								</Typography>
							</li>
						);
					})}
				</ol>
			</div>
		</div>
	);
};
